import { useQuery, useMutation } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { trustReconciliationApi } from '../api/trustReconciliationApi';
import type { TrustFiltersState } from '../types';
import { showToast } from '@repo/ui';

// Re-export types for backwards compatibility
export type { TrustFiltersState } from '../types';

/**
 * Hook for getting summary statistics
 */
export function useTrustSummary() {
  return useQuery({
    queryKey: ['trust-reconciliation', 'summary'],
    queryFn: () => trustReconciliationApi.getSummaryStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}

/**
 * Hook for getting trust transactions with pagination and filters
 */
export function useTrustTransactions(
  filters: TrustFiltersState,
  pagination: { page: number; limit: number }
) {
  return useQuery({
    queryKey: ['trust-reconciliation', 'transactions', filters, pagination],
    queryFn: () =>
      trustReconciliationApi.getTransactions({
        clientIds: filters.clients.length > 0 ? filters.clients : undefined,
        flags: filters.flags.length > 0 ? (filters.flags as any[]) : undefined,
        page: pagination.page,
        limit: pagination.limit,
      }),
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1,
  });
}

/**
 * Hook for getting client pivot data with pagination and filters
 */
export function useTrustPivotData(
  filters: TrustFiltersState,
  pagination: { page: number; limit: number }
) {
  return useQuery({
    queryKey: ['trust-reconciliation', 'pivot', filters, pagination],
    queryFn: () =>
      trustReconciliationApi.getClientPivot({
        clientIds: filters.clients.length > 0 ? filters.clients : undefined,
        page: pagination.page,
        limit: pagination.limit,
      }),
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1,
  });
}

/**
 * Progressive data loading hook
 * Loads data in stages: summary → pivot → transactions
 */
export function useTrustDataProgressive(
  activeTab: 'pivot' | 'transactions',
  options: {
    pivotFilters: TrustFiltersState;
    transactionFilters: TrustFiltersState;
    pivotPagination: { page: number; limit: number };
    transactionPagination: { page: number; limit: number };
  }
) {
  const [loadStage, setLoadStage] = useState<'summary' | 'pivot' | 'transactions'>('summary');

  // Load summary first
  const summaryQuery = useTrustSummary();

  // Load pivot data after summary is ready
  const pivotQuery = useTrustPivotData(
    options.pivotFilters,
    options.pivotPagination
  );

  // Load transaction data after summary is ready
  const transactionsQuery = useTrustTransactions(
    options.transactionFilters,
    options.transactionPagination
  );

  // Progressive loading logic
  useEffect(() => {
    if (summaryQuery.isSuccess && loadStage === 'summary') {
      // Summary loaded, start loading based on active tab
      if (activeTab === 'pivot') {
        setLoadStage('pivot');
      } else {
        setLoadStage('transactions');
      }
    }
  }, [summaryQuery.isSuccess, activeTab, loadStage]);

  return {
    summaryData: summaryQuery.data,
    pivotData: pivotQuery.data,
    transactionsData: transactionsQuery.data,
    loadingStates: {
      summary: summaryQuery.isLoading,
      pivot: pivotQuery.isLoading,
      transactions: transactionsQuery.isLoading,
    },
    error: summaryQuery.error || pivotQuery.error || transactionsQuery.error,
    refetch: () => {
      summaryQuery.refetch();
      pivotQuery.refetch();
      transactionsQuery.refetch();
    },
  };
}

/**
 * Hook for getting available clients
 */
export function useTrustClients() {
  return useQuery({
    queryKey: ['trust-reconciliation', 'clients'],
    queryFn: () => trustReconciliationApi.getAvailableClients(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
  });
}

/**
 * Hook for exporting trust reconciliation data
 */
export function useTrustExport() {
  return useMutation({
    mutationFn: (params: {
      type: 'csv' | 'xls' | 'pdf';
      clientIds?: string[];
    }) => trustReconciliationApi.exportData(params),
    onSuccess: () => {
      showToast.success('Export started successfully');
    },
    onError: (error) => {
      showToast.error(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    },
  });
}

